import React, { useEffect, useRef, useState } from "react";
import config from 'config.json';
import pointMarker from '../../../assets/images/tracking/Toll-Gates.svg';
import fastagLogo from 'assets/images/tracking/fastaglogohighresolution.png';
import lastPointMarker from '../../../assets/images/tracking/Current-Vehicle-Location.png';
import blurredMap from '../../../assets/images/blurred-map.webp';
import styles from "./styles.module.scss";

const HERE_SCRIPTS = [
  "https://js.api.here.com/v3/3.1/mapsjs-core.js",
  "https://js.api.here.com/v3/3.1/mapsjs-service.js",
  "https://js.api.here.com/v3/3.1/mapsjs-mapevents.js",
  "https://js.api.here.com/v3/3.1/mapsjs-ui.js"
];
const HERE_CSS = "https://js.api.here.com/v3/3.1/mapsjs-ui.css";

const FastagMapComponent = ({ vehicleData }) => {
  console.log("HIT", vehicleData)
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const platform = useRef(null);
  const markers = useRef([]);
  const group = useRef(null);
  const [showMap, setShowMap] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);

  const plotVehicleData = (map, ui) => {
    if (!vehicleData || vehicleData.length === 0) {
      console.log('No vehicle data to plot');
      return;
    }

    // Clear existing markers and route
    if (group.current) {
      map.removeObject(group.current);
    }
    
    group.current = new window.H.map.Group();

    const sortedData = [...vehicleData].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );

    const coordinates = sortedData.map(item => item.location.coordinates);
    
    // Create route line with improved colors
    if (coordinates.length > 1) {
      const lineString = new window.H.geo.LineString();
      coordinates.forEach(coord => {
        lineString.pushPoint({ lat: coord[1], lng: coord[0] });
      });

      const polyline = new window.H.map.Polyline(lineString, {
        style: {
          strokeColor: "#2D63B8", // Modern blue
          fillColor: "#3b82f6",   // Lighter blue for fill
          lineWidth: 6,
        }
      });
      
      group.current.addObject(polyline);
    }

    sortedData.forEach((item, index) => {
      const coord = item.location.coordinates;
      const isLast = index === sortedData.length - 1;
      
      let icon;
      
      if (isLast) {
        icon = new window.H.map.Icon(lastPointMarker);
      } else {
        icon = new window.H.map.Icon(pointMarker);
      }

      const marker = new window.H.map.Marker(
        { lat: coord[1], lng: coord[0] },
        { icon: icon }
      );

      // Set marker data for info bubble
      marker.setData(`
        <div style="min-width:150px">
          <span><b>${item.address}</b></span>
        </div>
        <div>
          <span>${new Date(item.timestamp).toLocaleString()}</span>
        </div>
        <div>
          <div>Long: ${coord[0] ? parseFloat(coord[0]).toFixed(4) : ""}</div>
          <div>Lat: ${coord[1] ? parseFloat(coord[1]).toFixed(4) : ""}</div>
        </div>
      `);

      group.current.addObject(marker);
      markers.current.push(marker);
    });

    // Add group to map
    map.addObject(group.current);

    // Fit map to show all markers
    if (coordinates.length > 0) {
      map.getViewModel().setLookAtData({
        bounds: group.current.getBoundingBox()
      });
    }

    // Add tap event listener for info bubbles
    group.current.addEventListener('tap', function (evt) {
      let bubble = new window.H.ui.InfoBubble(evt.target.getGeometry(), {
        content: evt.target.getData()
      });
      ui.addBubble(bubble);
    }, false);
  };

  const initMap = () => {
    if (!mapRef.current || mapInstance.current || !showMap) return;

    platform.current = new window.H.service.Platform({
      apikey: config.HERE_API_KEY
    });

    const defaultLayers = platform.current.createDefaultLayers();

    let center = { lat: 20.5937, lng: 78.9629 };
    let zoom = 6;

    if (vehicleData && vehicleData.length > 0) {
      const coordinates = vehicleData.map(item => item.location.coordinates);
      const lats = coordinates.map(coord => coord[1]);
      const lngs = coordinates.map(coord => coord[0]);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      center = { lat: (minLat + maxLat) / 2, lng: (minLng + maxLng) / 2 };

      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);

      if (maxDiff > 5) zoom = 4;
      else if (maxDiff > 2) zoom = 6;
      else if (maxDiff > 1) zoom = 8;
      else zoom = 10;
    }

    const newMap = new window.H.Map(
      mapRef.current,
      defaultLayers.vector.normal.map,
      {
        zoom: zoom,
        center: center,
        padding: { top: 60, left: 60, bottom: 60, right: 60 }
      }
    );

    const mapEvents = new window.H.mapevents.MapEvents(newMap);
    const ui = window.H.ui.UI.createDefault(newMap, defaultLayers);

    window.addEventListener('resize', () => newMap.getViewPort().resize());

    mapInstance.current = newMap;
    setMapLoaded(true);

    if (vehicleData && vehicleData.length > 0) {
      plotVehicleData(newMap, ui);
    }
  };

  const handleViewMapClick = () => {
    setShowMap(!showMap);
  };

  useEffect(() => {
    if (!showMap) return;

    let isMounted = true;
    const loadCss = (href) =>
      new Promise((resolve) => {
        if (document.querySelector(`link[href="${href}"]`)) return resolve();
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.onload = () => resolve();
        link.onerror = () => resolve();
        document.head.appendChild(link);
      });

    const loadScript = (src) =>
      new Promise((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const script = document.createElement('script');
        script.src = src;
        script.async = false;
        script.onload = () => resolve();
        script.onerror = (e) => reject(e);
        document.head.appendChild(script);
      });

    const loadAll = async () => {
      try {
        await loadCss(HERE_CSS);
        await Promise.all(HERE_SCRIPTS.map(loadScript));
        if (!isMounted) return;
        initMap();
      } catch (err) {
        console.error('Error loading HERE SDK', err);
      }
    };

    loadAll();

    return () => {
      isMounted = false;
      if (mapInstance.current) {
        try { mapInstance.current.dispose(); } catch (e) {}
        mapInstance.current = null;
        setMapLoaded(false);
      }
    };
  }, [showMap]);

  const renderViewMapButton = () => {
    return (
      <div className={styles.mapButton}>
        <button onClick={handleViewMapClick} className={styles.beautifyButton}>
          <div className={styles.buttonContent}>
            <img src={fastagLogo} alt="Fastag Logo" className={styles.ftLogo} />
            <span>View Map</span>
          </div>
        </button>
      </div>
    );
  };

  return (
    <div style={{ width: "100%", height: "400px", position: "relative" }}>
      {!showMap ? (
        <div style={{backgroundImage: `url(${blurredMap})`}} className={styles.mapButtonBox}>
          {renderViewMapButton()}
        </div>
      ) : (
        <>
          <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
        </>
      )}
    </div>
  );
};

export default FastagMapComponent;
